/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { InsCompResponse, pmiServicing, PmiServicingResponse } from '../common/queryFunctions';
import { useCallback, useEffect, useState, FormEvent, useRef } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useViewingContext } from '../contexts/ViewingContext';
import useInsCompInfo from '../common/hooks/useInsCompInfo';
import { borrowerNoAccessMsg, pmiCompanyCodes } from '../common/constants';
import css from '../css/PmiServicing.module.css';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { isStrEmptyOrNull } from '../common/UtilityFunctions';
import DefaultModal from './modals/DefaultModal';

interface PmiServicingRequestData {
  pmiCompany: string;
  opType: string;
  isTest: string; //SQC-490 Jacques Bega, string boolean denoting whether a request is a test request or a production request
}
interface FormInput {
  pmiCompany: string;
  opType: string;
}
const PmiServicing: React.FC = () => {
  const { token } = useAuthContext();
  const { isServicerView } = useViewingContext();

  //state variables
  const [pmiCompanies, setPmiCompanies] = useState<Array<InsCompResponse>>();
  const abortControllerRef = useRef<AbortController | null>(null);
  const [formInput, setFormInput] = useState<FormInput>({
    pmiCompany: '',
    opType: '',
  });

  const {
    data: pmiCompanyInfo,
    status: pmiCompanyStatus,
    error: pmiCompanyErrors,
  } = useInsCompInfo(pmiCompanyCodes, token, 'pmiInsCompInfo');

  useEffect(() => {
    if (pmiCompanyStatus === 'pending' || pmiCompanyStatus === 'error' || !pmiCompanyInfo) {
      setPmiCompanies([]);
    } else {
      setPmiCompanies(pmiCompanyInfo);
    }
  }, [pmiCompanyInfo, pmiCompanyStatus]);

  const pmiServicingMutation: UseMutationResult<PmiServicingResponse, Error, PmiServicingRequestData, unknown> =
    useMutation({
      mutationFn: (requestData: PmiServicingRequestData) => {
        const controller: AbortController = new AbortController();
        abortControllerRef.current = controller;
        const signal: AbortSignal = controller.signal;
        const mutationPromise: Promise<PmiServicingResponse> = pmiServicing(
          signal,
          token,
          requestData.opType,
          requestData.pmiCompany,
          requestData.isTest, //SQC-490 Jacques Bega, string boolean denoting whether a request is a test request or a production request
        );
        (mutationPromise as any).cancel = () => controller.abort();
        return mutationPromise;
      },
    });

  const clearFormInput = useCallback(() => {
    setFormInput({
      pmiCompany: '',
      opType: '',
    });
  }, []);

  const resetMutations = useCallback(() => {
    //reset mutations' status
    pmiServicingMutation.reset();
  }, [pmiServicingMutation]);

  const reset = useCallback(() => {
    clearFormInput();
    resetMutations();
  }, [clearFormInput, resetMutations]);

  const StatusModal = () => {
    const mutationErrorsPresent: boolean = pmiServicingMutation.status === 'error';
    const mutationSuccessPresent: boolean = pmiServicingMutation.status === 'success';
    const mutationPending: boolean = pmiServicingMutation.status === 'pending';

    let titleStr: string = '';
    if (mutationErrorsPresent) titleStr = 'Error';
    else if (mutationSuccessPresent) titleStr = 'Success';
    else if (mutationPending) titleStr = 'Processing...';

    return (
      <DefaultModal
        open={mutationErrorsPresent || mutationSuccessPresent || mutationPending}
        title={<div className={css.modalTitle}>{titleStr}</div>}
        content={
          <div className={css.modalContent}>
            {pmiServicingMutation.status === 'error' && <div>{pmiServicingMutation.error.message}</div>}
            {mutationSuccessPresent && <div>{pmiServicingMutation.data.responseData.response}</div>}
          </div>
        }
        footer={
          (mutationErrorsPresent || mutationSuccessPresent) && (
            <button className={css.modalButton} onClick={reset}>
              {'OK'}
            </button>
          )
        }
      ></DefaultModal>
    );
  };

  const updateFormInput = useCallback((event: { target: { name: string; value: string; type: string } }) => {
    const name: string = event.target.name;
    const value: string = event.target.value;

    console.log(name);
    console.log(value);

    setFormInput((prevFormInput) => {
      const newFormInput: FormInput = { ...prevFormInput };
      newFormInput[name] = value;
      return newFormInput;
    });
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      pmiServicingMutation.reset();
      console.log(formInput);
      if (isStrEmptyOrNull(formInput.pmiCompany)) {
        alert('PMI Company must be selected.');
        return;
      }
      if (isStrEmptyOrNull(formInput.opType)) {
        alert('Operation must be selected');
        return;
      }

      //SQC-490 Jacques Bega
      //isTest is set to zero so that PMI payment files are labeled for production
      const requestdata: PmiServicingRequestData = { pmiCompany: '', opType: '', isTest: '0' };
      requestdata.pmiCompany = formInput.pmiCompany;
      requestdata.opType = formInput.opType;
      pmiServicingMutation.mutate(requestdata);
    },
    [formInput, pmiServicingMutation],
  );

  if (!isServicerView) return <div className='access-error-msg'>{borrowerNoAccessMsg}</div>;

  if (pmiCompanyStatus === 'pending') return <div>Loading...</div>;
  if (pmiCompanyErrors.length > 0)
    return (
      <>
        <h2>Encountered errors while obtaining PMI Companye Information.</h2>
        <ul>
          {pmiCompanyErrors.map((queryError, index) => {
            return <li key={index}>{queryError.message}</li>;
          })}
        </ul>
      </>
    );
  return (
    <>
      <form className={css.pmiServicingForm} onSubmit={handleSubmit}>
        <label>
          <span>Select PMI Company:</span>
          <select name='pmiCompany' value={formInput.pmiCompany} onChange={updateFormInput}>
            <option value='' disabled>
              -- Choose an option --
            </option>
            {pmiCompanies &&
              pmiCompanies.length &&
              pmiCompanies.map((pmiCompany, index) => (
                <option key={index} value={pmiCompany.compCode}>
                  {`${pmiCompany.compCode} - ${pmiCompany.name1}`}
                </option>
              ))}
          </select>
        </label>
        <div className={css.operationTypeFieldSet}>
          <span>Select Operation:</span>
          <label>
            <input
              type='radio'
              name='opType'
              value='update'
              checked={formInput.opType === 'update'}
              onChange={updateFormInput}
            ></input>
            Update Current Month PMI Report
          </label>
          <label>
            <input
              type='radio'
              name='opType'
              value='payment'
              checked={formInput.opType === 'payment'}
              onChange={updateFormInput}
            ></input>
            Pay Current Month PMI Bill
          </label>
        </div>
        <button className={css.pmiServicingFormSubmitButton} type='submit'>
          Submit
        </button>
      </form>
      {StatusModal()}
    </>
  );
};

export default PmiServicing;
